// web/js/player-columns/playerColumnManager.ts
import type {
  ColumnId, ColumnPlayerHandle, ColumnState, PlayerColumnManager,
  PlayerColumnManagerProps, Snapshot, SubscriberCallback, TabConfig,
} from './types';
import type { GetFileResponse } from '../player-loader/loadFile';
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { PlayerColumn } from './PlayerColumn';

export interface ColumnHandle {
  unmount(): void;
  rerender(props: { isActive: boolean; isOnly: boolean; fileId: number }): void;
  /**
   * Returns the `.player-stage` DOM element for this column's current file,
   * or null if the column hasn't rendered a stage yet (e.g. the initial
   * fetch is still in flight, or the current file is a non-media viewer
   * that doesn't render a stage).
   */
  getStageElement(): HTMLElement | null;
  /**
   * Returns the latest `GetFileResponse` loaded by this column, or null
   * if no file data has been loaded yet. Captured in a closure by the
   * mounter so the manager can surface it synchronously to legacy code.
   */
  getFileData(): GetFileResponse | null;
  /**
   * Returns a typed handle to this column's current viewer API, or null
   * if no viewer has been mounted yet (the initial fetch is still in
   * flight, the column has no file data). Populated by createDefaultMounter
   * from the `onViewerReady` callback that PlayerSlot fires on each
   * viewer mount.
   */
  getPlayer(): ColumnPlayerHandle | null;
  /** Returns the column's wrapper DOM element (the `.player-column` div). */
  getElement(): HTMLElement;
}

export interface MounterDeps {
  mountColumn(args: {
    colId: ColumnId;
    fileId: number;
    linkId: string;
    isFolderLink: boolean;
    linkName: string;
    linkRelativePath: string;
    initialData?: GetFileResponse;
    isActive: boolean;
    isOnly: boolean;
    onSelect: (colId: ColumnId) => void;
    onFileChange: (colId: ColumnId, newFileId: number) => void;
    onFileDataLoaded?: (data: GetFileResponse | null) => void;
    onViewerReady?: (handle: ColumnPlayerHandle) => void;
    onAddClick?: () => void;
    onCloseClick?: (colId: ColumnId) => void;
    onAddVersionClick?: (fileId: number) => void;
    autoplay: boolean;
    onWorkflowChange?: (workflowId: number) => void;
    tabConfig: TabConfig;
  }): ColumnHandle;
  container: HTMLElement | null;
}

export function createPlayerColumnManager(props: PlayerColumnManagerProps, deps: MounterDeps): PlayerColumnManager {
  let nextId = 1;
  const columns: Array<ColumnState & { handle: ColumnHandle }> = [];
  let activeColId: ColumnId | null = null;
  const subscribers = new Set<SubscriberCallback>();
  let switchGuard: (() => boolean) | undefined;

  const snapshot = (): Snapshot => ({
    columns: columns.map(c => ({ colId: c.colId, fileId: c.fileId })),
    activeColId,
    activeFileId: columns.find(c => c.colId === activeColId)?.fileId ?? null,
  });

  // Subscribers must not call back into any manager mutation method during a
  // notify pass — reentrancy would deliver a stale snapshot to later subscribers.
  const notify = () => {
    const snap = snapshot();
    subscribers.forEach(cb => cb(snap));
  };

  const rerenderAll = (skipColId?: ColumnId) => {
    const isOnly = columns.length === 1;
    columns.forEach(c => {
      if (c.colId === skipColId) return;
      c.handle.rerender({
        isActive: c.colId === activeColId,
        isOnly,
        fileId: c.fileId,
      });
    });
  };

  const openColumn = (fileId: number, initialData?: GetFileResponse, makeActive: boolean = true, autoplay: boolean = true): ColumnId => {
    const colId = `col-${nextId++}`;
    const hadActive = activeColId !== null;
    const willBeActive = makeActive || !hadActive;

    // Guard: if switching away from the current active viewer, check the guard
    if (willBeActive && hadActive && switchGuard && !switchGuard()) {
      // Abort — still open the column but don't make it active
      makeActive = false;
    }

    // Recompute willBeActive in case makeActive was just flipped by the guard
    const willBeActiveFinal = makeActive || !hadActive;

    // If the new column will become active, DEMOTE the existing active
    // column first — i.e. rerender it with `isActive: false` BEFORE we
    // mount the new column. This gives the previously active column's
    // viewer (KollabPlayer / KollabImageViewer) a chance to release any
    // singleton DOM resources it owns (e.g. the `#comment-tabs` side
    // panel) before the new column's mount effects try to claim them.
    // Without this ordering, two columns can race for ownership of the
    // same DOM node and crash React's reconciler.
    if (willBeActiveFinal && hadActive) {
      const prevActive = activeColId;
      activeColId = null;
      const isOnlyAfter = false; // we're about to add a second column
      columns.forEach(c => {
        if (c.colId === prevActive) {
          c.handle.rerender({ isActive: false, isOnly: isOnlyAfter, fileId: c.fileId });
        }
      });
    }

    const handle = deps.mountColumn({
      colId, fileId,
      linkId: getLinkId(),
      initialData,
      isFolderLink: initialData?.link?.is_folder_link ?? false,
      linkName: initialData?.link?.link_name ?? null,
      linkRelativePath: initialData?.link?.link_relative_path ?? '/',
      isActive: willBeActiveFinal,
      isOnly: columns.length === 0,
      onSelect: (id) => setActive(id),
      onFileChange: (id, newId) => onFileChange(id, newId),
      onFileDataLoaded: (data) => {
        // When any column's file data loads or changes, notify
        // subscribers so legacy code (e.g. the comments version dropdown)
        // can re-derive from the new data.
        notify();
      },
      onViewerReady: (handle) => {
        // Notify subscribers when any column's viewer API becomes
        // available. This mirrors the onFileDataLoaded handler so legacy
        // code subscribed to manager changes can react to the player
        // becoming drivable.
        notify();
      },
      onAddClick: () => onAddClick(),
      onCloseClick: (id) => onCloseClick(id),
      onAddVersionClick: (fileId: number)=> {
        openColumn(fileId, undefined, false, false);
      },
      autoplay: autoplay,
      onWorkflowChange: (workflowId: number) => onWorkflowChange(fileId, workflowId),
      tabConfig: props?.tabConfig,
    });
    columns.push({ colId, fileId, handle });
    if (willBeActiveFinal) activeColId = colId;
    // Skip re-rendering the column we just mounted — its initial render
    // already uses the correct props, and rerendering it would drop any
    // `initialData` that was passed in.
    rerenderAll(colId);
    notify();
    return colId;
  };

  const closeColumn = (colId: ColumnId) => {
    const idx = columns.findIndex(c => c.colId === colId);
    if (idx === -1) return;
    columns[idx].handle.unmount();
    columns.splice(idx, 1);
    if (activeColId === colId) {
      // After splice at idx: left neighbor is at idx-1 (unchanged), right neighbor is now at idx.
      // Prefer left, fall back to right, fall back to null.
      const neighbor = columns[idx - 1] ?? columns[idx] ?? null;
      activeColId = neighbor?.colId ?? null;
    }
    rerenderAll();
    notify();
  };

  const setActive = (colId: ColumnId) => {
    if (activeColId === colId) return;
    if (!columns.some(c => c.colId === colId)) return;
    // Guard: allow callers to abort the switch (e.g. unsaved annotations)
    if (switchGuard && !switchGuard()) return;
    // Demote the previously-active column FIRST, so it releases any
    // singleton DOM resources (e.g. the `#comment-tabs` side panel)
    // before the newly-active column tries to adopt them. See the
    // matching comment in `openColumn` for the full rationale.
    const prevActive = activeColId;
    activeColId = null;
    const isOnly = columns.length === 1;
    if (prevActive) {
      columns.forEach(c => {
        if (c.colId === prevActive) {
          c.handle.rerender({ isActive: false, isOnly, fileId: c.fileId });
        }
      });
    }
    activeColId = colId;
    rerenderAll();
    notify();
  };

  const onFileChange = (colId: ColumnId, newFileId: number) => {
    const col = columns.find(c => c.colId === colId);
    if (!col) return;
    col.fileId = newFileId;
    // Re-render so PlayerColumn's React tree sees the new fileId prop
    // and its useEffect refetches. Without this, version-pick from inside
    // a column would update the manager's state but leave the UI stale.
    rerenderAll();
    // Always notify when there are multiple columns so the URL compare
    // param and sync subscriptions update regardless of which column
    // changed. With a single column, only notify if it's the active one
    // (preserves the original behavior for the single-column case).
    if (activeColId === colId || columns.length > 1) notify();
  };

  const subscribe = (cb: SubscriberCallback) => {
    subscribers.add(cb);
    return () => { subscribers.delete(cb); };
  };

  /**
   * Load a file into the active column. If there is no active column, opens a
   * new one for the file and returns its colId.
   */
  const loadFileIntoActiveColumn = (fileId: number): ColumnId => {
    if (activeColId === null) return openColumn(fileId);
    const col = columns.find(c => c.colId === activeColId);
    if (!col) return activeColId;
    col.fileId = fileId;
    rerenderAll();
    notify();
    return activeColId;
  };

  const reloadFile = (fileId: number): void => {
    const col = columns.find(c => c.fileId === fileId);
    if (!col) return;
    rerenderAll();
    notify();
  };

  const reloadColumn = (colId: ColumnId): void => {
    const col = columns.find(c => c.colId === colId);
    if (!col) return;
    rerenderAll();
    notify();
  };

  const getActiveStageElement = (): HTMLElement | null => {
    const col = columns.find(c => c.colId === activeColId);
    return col?.handle.getStageElement() ?? null;
  };

  const getActiveFileData = (): GetFileResponse | null => {
    const col = columns.find(c => c.colId === activeColId);
    return col?.handle.getFileData() ?? null;
  };

  const getColumnFileData = (colId: ColumnId): GetFileResponse | null => {
    const col = columns.find(c => c.colId === colId);
    return col?.handle.getFileData() ?? null;
  };

  const getColumnPlayer = (colId: ColumnId): ColumnPlayerHandle | null => {
    const col = columns.find(c => c.colId === colId);
    return col?.handle.getPlayer() ?? null;
  };

  const getActiveColumnPlayer = (): ColumnPlayerHandle | null => {
    if (activeColId === null) return null;
    return getColumnPlayer(activeColId);
  };

  const getColumnIdForFileId = (fileId: number): ColumnId | null => {
    const col = columns.find(c => c.fileId === fileId);
    return col?.colId ?? null;
  };

  const getPlayerForFileId = (fileId: number): ColumnPlayerHandle | null => {
    const col = columns.find(c => c.fileId === fileId);
    return col?.handle.getPlayer() ?? null;
  };

  const getActiveColumnElement = (): HTMLElement | null => {
    const col = columns.find(c => c.colId === activeColId);
    return col?.handle.getElement() ?? null;
  };

  const getColumnElement = (colId: ColumnId): HTMLElement | null => {
    const col = columns.find(c => c.colId === colId);
    return col?.handle.getElement() ?? null;
  };

  const onAddClick = () => {
    const w = window as unknown as {
      showFileBrowser?: (
        excludeId: number,
        cb: (
          itemID: number,
          itemTitle: string,
          itemIconURL: string,
          deptID: number,
          folderPath: string,
          deptName: string,
          projectID: number,
        ) => void,
        help_text: string,
        dialog_title: string,
      ) => void;
    };
    if (typeof w.showFileBrowser !== 'function') return;
    w.showFileBrowser(-1, (itemID) => {
      // Pause the existing video
      const player = getActiveColumnPlayer();
      if (player && (player.type === 'video' || player.type === 'audio') && player.api) {
        player.api.pause();
      }

      // makeActive=false so the existing column keeps focus while a new
      // comparison column is opened to the right of it.
      openColumn(itemID, undefined, false, false);
    }, 'Choose a file to compare:', 'Compare Files');
  };

  const onCloseClick = (id: ColumnId) => {
    closeColumn(id);
  };

  const onWorkflowChange = async (fileId: number, workflowId: number) => {
    const params = new URLSearchParams();
    params.append('ids', fileId.toString());
    params.append('workflow', workflowId.toString());

    try {
//      const response = await fetch(`/file/${fileId}/workflow`, {method: 'POST', body: params});
      const response = await fetch(`/ajax/file_ops`, {method: 'POST', body: params});
      const json = await response.json();

      if (json.success) {
        // Fetch new workflow data after delay to prevent race condition
        window.setTimeout(() => {
          window.refreshApprovalPanel?.();
        }, 500);
      } else if (json.error) {
        console.error(json.error);
        alert(`Failed to change workflow: ${json.error}`);
      } else {
        console.log('Unknown error');
        alert('Failed to change workflow: Unknown error');
      }
    } catch (e) {
      console.error(e);
      alert('Failed to change workflow: Network error');
    }
  };

  const manager: PlayerColumnManager = {
    openColumn,
    closeColumn,
    setActive,
    getActiveColumnId: () => snapshot().activeColId,
    getActiveFileId: () => snapshot().activeFileId,
    getSnapshot: snapshot,
    subscribe,
    loadFileIntoActiveColumn,
    getActiveStageElement,
    getActiveFileData,
    getColumnFileData,
    getColumnPlayer,
    getActiveColumnPlayer,
    getPlayerForFileId,
    getColumnIdForFileId,
    getActiveColumnElement,
    getColumnElement,
    reloadFile,
    reloadColumn,
    get shouldSwitchActiveViewer() { return switchGuard; },
    set shouldSwitchActiveViewer(fn: (() => boolean) | undefined) { switchGuard = fn; },
  };
  return manager;
}

export function createDefaultMounter(container: HTMLElement): MounterDeps['mountColumn'] {
  return function mountColumn(args) {
    const colDiv = document.createElement('div');
    colDiv.className = 'player-column';
    colDiv.dataset.colId = args.colId;
    container.appendChild(colDiv);
    const root: Root = createRoot(colDiv);

    // `initialData` is intentionally NOT stored in `currentProps` —
    // it's only used on the first mount. `rerender` never forwards it,
    // so subsequent fileId changes (version switches) go through the
    // normal fetch path.
    // Closure-captured cache of the latest file data loaded by the
    // PlayerColumn React tree. Populated by the onFileDataLoaded callback
    // we pass into the props so getFileData() can return it synchronously.
    let latestFileData: GetFileResponse | null = args.initialData ?? null;
    let latestPlayerHandle: ColumnPlayerHandle | null = null;

    let currentProps: {
      colId: ColumnId;
      fileId: number;
      linkId: string;
      isFolderLink?: boolean;
      initialData?: GetFileResponse;
      isActive: boolean;
      isOnly: boolean;
      onSelect: (colId: ColumnId) => void;
      onFileChange: (colId: ColumnId, newFileId: number) => void;
      onFileDataLoaded: (data: GetFileResponse | null) => void;
      onViewerReady: (handle: ColumnPlayerHandle) => void;
      onAddClick?: () => void;
      onCloseClick?: (colId: ColumnId) => void;
      onAddVersionClick?: (fileId: number) => void;
      autoplay: boolean;
      onWorkflowChange: (workflowId: number) => void;
      tabConfig?: TabConfig;
    } = {
      colId: args.colId,
      fileId: args.fileId,
      linkId: args.linkId,
      isFolderLink: args.isFolderLink,
      initialData: args.initialData,
      isActive: args.isActive,
      isOnly: args.isOnly,
      onSelect: args.onSelect,
      onFileChange: args.onFileChange,
      onFileDataLoaded: (data) => {
        latestFileData = data;
        args.onFileDataLoaded?.(data);
      },
      onViewerReady: (handle: ColumnPlayerHandle) => {
        latestPlayerHandle = handle;
        args.onViewerReady?.(handle);
      },
      onAddClick: args.onAddClick,
      onCloseClick: args.onCloseClick,
      onAddVersionClick: args.onAddVersionClick,
      autoplay: args.autoplay,
      onWorkflowChange: (workflowId: number) => args.onWorkflowChange?.(workflowId),
      tabConfig: args.tabConfig,
    };
    const syncAttrs = () => {
      colDiv.dataset.active = String(currentProps.isActive);
      colDiv.dataset.only = String(currentProps.isOnly);
      colDiv.dataset.fileId = String(currentProps.fileId);
    };
    const renderTree = () => {
      syncAttrs();
      root.render(React.createElement(PlayerColumn, currentProps));
    };
    renderTree();

    return {
      unmount: () => {
        root.unmount();
        colDiv.remove();
      },
      rerender: ({ isActive, isOnly, fileId }) => {
        // Drop initialData on rerender — it's only for the first mount.
        currentProps = { ...currentProps, isActive, isOnly, fileId, initialData: undefined };
        renderTree();
      },
      getStageElement: () => colDiv.querySelector('.player-stage') as HTMLElement | null,
      getFileData: () => latestFileData,
      getPlayer: () => latestPlayerHandle,
      getElement: () => colDiv,
    };
  };
}

export function attachPlayerColumnManager(container: HTMLElement, props: PlayerColumnManagerProps = null): PlayerColumnManager {
  return createPlayerColumnManager(props, {
    container,
    mountColumn: createDefaultMounter(container),
  });
}

function getLinkId(): string | null {
  const w = window as any;
  if (w.jsconnect?.link?.id) return String(w.jsconnect.link.id);
  return null;
}

declare global {
  interface Window {
    attachPlayerColumnManager?: typeof attachPlayerColumnManager;
    playerColumns?: PlayerColumnManager;
    refreshApprovalPanel?: () => void;
  }
}

if (typeof window !== 'undefined') {
  window.attachPlayerColumnManager = attachPlayerColumnManager;
}
